<script setup lang="ts">
import _, { isString } from 'lodash'
import { clamp, slugify } from '../util'
import { resolveDice, type DiceExpr } from '~/assets/dice/diceEval'

const props = withDefaults(
  defineProps<{
    uses: DiceExpr
    name?: string
    lr?: DiceExpr | 'reset'
    sr?: DiceExpr | 'reset'
    hideReset?: boolean
    noparens?: boolean
    average?: boolean
    reverse?: boolean
  }>(),
  {
    uses: 1,
    lr: 'reset',
    sr: '',
    name: '',
    reverse: false,
    average: false,
  }
)

const mods = useMods()

const elem = ref<HTMLElement | null>(null)
const resolvedName = computed(() => {
  if (props.name) return props.name
  const title = elem.value?.parentElement?.querySelector('strong:first-child')?.textContent
  if (title) {
    return slugify(title)
  }
  return ''
})

const expended = useResource(resolvedName)

const delta = ref(1)

const total = computed(() => resolveDice(props.uses, mods(), props.average))
const totalExpended = computed(() => {
  if (props.reverse) return total.value - expended.value
  return expended.value
})

const usesRange = computed(() => _.range(0, total.value))
const resetText = computed(() => {
  if (props.hideReset) return ''
  if (props.sr === 'reset') return ' / sr'
  if (props.lr === 'reset') return ' / lr'
  return ''
})

onUseResource(resolvedName, (_, amount) => {
  expended.value = clamp(expended.value + amount, 0, total.value)
})

const resolveReset = (reset: DiceExpr | 'reset') => {
  if (reset === 'reset') {
    return 0
  } else if (isString(reset) && reset.startsWith('=')) {
    const direct = resolveDice(reset.slice(1))
    return clamp(direct, 0, total.value)
  } else {
    const delta = resolveDice(reset)
    return clamp(expended.value - delta, 0, total.value)
  }
}

onRest('lr', () => {
  expended.value = resolveReset(props.lr)
})

onRest('sr', () => {
  expended.value = resolveReset(props.sr)
})

const handleClick = (use: number) => {
  let change = delta.value
  if (!change) return
  if (props.reverse) change *= -1
  if (use < expended.value) {
    expended.value = clamp(expended.value - change, 0, total.value)
  } else {
    expended.value = clamp(expended.value + change, 0, total.value)
  }
}
</script>

<template>
  <ClientOnly>
    <span v-if="total < 10" ref="elem" class="counter">
      {{ !noparens ? '(' : ''
      }}<span class="checkboxes">
        <Icon
          v-for="use in usesRange"
          :key="use"
          :name="expended > use ? 'fa6-solid:square-check' : 'fa6-regular:square'"
          class="use-checkbox"
          @click.prevent="handleClick(use)"
        /> </span
      ><span class="reset">{{ resetText }}</span
      ><span class="extra"><slot></slot></span
      >{{ !noparens ? ')' : '' }}
    </span>
    <span v-else ref="elem" class="counter bar">
      <div class="progress-bar">
        <div class="bar" :style="{ width: (100 * (total - totalExpended)) / total + '%' }"></div>
        <span class="label title-font">{{ total - totalExpended }} / {{ total }}</span>
      </div>
      <button @click.prevent="handleClick(Infinity)">
        <Icon name="estia:minus" />
      </button>
      <input v-if="total > 20" v-model.number="delta" type="text" pattern="[0-9]+" />
      <button @click.prevent="handleClick(-1)">
        <Icon name="estia:plus" />
      </button>
    </span>
  </ClientOnly>
</template>

<style scoped lang="postcss">
.counter {
  white-space: pre;

  .reset {
    color: var(--text-highlight);
    font-size: 0.8em;
    text-transform: uppercase;
    font-weight: 500;
  }

  .extra {
    &::before {
      content: ' ';
    }

    &:empty {
      display: none;
    }
  }

  .checkboxes {
    display: inline-flex;
    margin: 0;
    padding-bottom: 1px;
    vertical-align: bottom;

    .icon {
      padding: 0 1px;
      height: 1rem;
      width: 1rem;
      cursor: pointer;
      color: var(--text-highlight);
      display: inline-flex;
      align-items: center;
    }
  }

  &.bar {
    display: inline-flex;
    width: 100%;
    gap: 4px;
    margin-top: 4px;

    .progress-bar {
      flex: 1;
      position: relative;
      text-align: center;
      border-radius: 5px;
      height: 26px;
      border: 1px solid var(--primary);

      .bar {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        background: var(--primary);
        z-index: 5;
        transition: width 0.25s ease-out;
      }

      .label {
        position: absolute;
        top: 0;
        z-index: 10;
        font-family: var(--font-stack-title);
        color: var(--text-highlight);
        left: 50%;
        transform: translateX(-50%);
      }
    }

    input {
      padding: 0;
      margin: 0;
      text-align: center;
      width: 3em;
      font-family: var(--font-stack-title);
      color: var(--text-highlight);
      background: var(--background);
      border: 1px solid var(--primary);
      border-bottom: 4px solid var(--primary);
      border-radius: 4px;

      &:focus-visible {
        outline: none;
        box-shadow: inset 0 0 5px var(--primary);
      }
    }

    button {
      padding: 0;
      margin: 0;
      width: 2em;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>

<style lang="postcss">
.spellcasting--innate {
  .counter {
    margin-left: 0.25em;
  }
}
</style>
